/**
 * Product API Service
 * Fetches product data from MongoDB via backend API
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch all machines/products
 * @param {string} category - Optional category filter
 */
export async function getMachines(category = null) {
  try {
    let url = `${API_URL}/api/products/machines`;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch machines');
    return await response.json();
  } catch (error) {
    console.error('Error fetching machines:', error);
    return [];
  }
}

/**
 * Fetch a single machine by slug
 * @param {string} slug - Product slug
 */
export async function getMachineBySlug(slug) {
  try {
    const response = await fetch(`${API_URL}/api/products/machines/${slug}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch machine');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching machine:', error);
    return null;
  }
}

/**
 * Fetch all accessories
 */
export async function getAccessories() {
  try {
    const response = await fetch(`${API_URL}/api/products/accessories`);
    if (!response.ok) throw new Error('Failed to fetch accessories');
    return await response.json();
  } catch (error) {
    console.error('Error fetching accessories:', error);
    return [];
  }
}

/**
 * Fetch a single accessory by slug
 * @param {string} slug - Accessory slug
 */
export async function getAccessoryBySlug(slug) {
  try {
    const response = await fetch(`${API_URL}/api/products/accessories/${slug}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch accessory');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching accessory:', error);
    return null;
  }
}

/**
 * Fetch company info
 */
export async function getCompanyInfo() {
  try {
    const response = await fetch(`${API_URL}/api/products/company-info`);
    if (!response.ok) throw new Error('Failed to fetch company info');
    return await response.json();
  } catch (error) {
    console.error('Error fetching company info:', error);
    return null;
  }
}

/**
 * Fetch hero images
 */
export async function getHeroImages() {
  try {
    const response = await fetch(`${API_URL}/api/products/hero-images`);
    if (!response.ok) throw new Error('Failed to fetch hero images');
    return await response.json();
  } catch (error) {
    console.error('Error fetching hero images:', error);
    return null;
  }
}

/**
 * Fetch services
 */
export async function getServices() {
  try {
    const response = await fetch(`${API_URL}/api/products/services`);
    if (!response.ok) throw new Error('Failed to fetch services');
    return await response.json();
  } catch (error) {
    console.error('Error fetching services:', error);
    return [];
  }
}

/**
 * Fetch product categories
 */
export async function getCategories() {
  try {
    const response = await fetch(`${API_URL}/api/products/categories`);
    if (!response.ok) throw new Error('Failed to fetch categories');
    return await response.json();
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
}

/**
 * Search products
 * @param {string} query - Search query
 */
export async function searchProducts(query) {
  try {
    const response = await fetch(`${API_URL}/api/products/search?q=${encodeURIComponent(query)}`);
    if (!response.ok) throw new Error('Failed to search products');
    return await response.json();
  } catch (error) {
    console.error('Error searching products:', error);
    return { machines: [], accessories: [], total: 0 };
  }
}

/**
 * Get Mini-Bagger shared videos
 */
export async function getMiniBaggerVideos() {
  try {
    const response = await fetch(`${API_URL}/api/products/mini-bagger-videos`);
    if (!response.ok) throw new Error('Failed to fetch videos');
    return await response.json();
  } catch (error) {
    console.error('Error fetching videos:', error);
    return [];
  }
}
